import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { db, auth } from "../firebase";
import { Timestamp, doc, getDoc, onSnapshot, updateDoc, arrayRemove } from "firebase/firestore";
import "../styles/Modal.css";
import "../styles/Devis.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FirestoreDevis } from "../types/firestore";
import DevisModal from "../components/DevisModal";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { CSVLink } from "react-csv";
import { getPDFCoordinatesFromStorage } from "../utils/pdfCoordinates";
import { PDF_SCALE, PDF_FONT_SIZE_OFFSET, Y_OFFSET_CORRECTION } from "../constants/pdfConfig";
import { Y_OFFSET_PDF_GENERATION, X_OFFSET_PDF_GENERATION } from "../constants/fdaConfig";
import { mmToPixels } from "../utils/units";
import { useNavigate, useLocation } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { useDemoContext } from "../context/DemoContext";

type FieldSource = 'devis' | 'opportunities' | 'society' | 'hillebrand';

type FieldData = {
  x: number;
  y: number;
  size?: number;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  customText?: string;
  isCustomField?: boolean;
};

interface Society {
  name?: string;
  siret?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  [key: string]: any; // Signature d'index pour accès dynamique
}

interface Opportunity {
  type?: string;
  [key: string]: any;
}

// Interface pour la modale de contact
interface ContactModalType {
  name: string;
  phone?: string;
  email?: string;
  fonction?: string;
  gender?: string;
  principalContact?: string;
  principalFirstName?: string;
}

const Devis: React.FC = () => {
  usePageTitle("Devis");
  const { companiesDocId, isDemoMode } = useDemoContext();
  const location = useLocation();
  // États
  const [devisList, setDevisList] = useState<FirestoreDevis[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [devisToEdit, setDevisToEdit] = useState<FirestoreDevis | undefined>(undefined);
  const [preselectedOpportunity, setPreselectedOpportunity] = useState<any | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [currentPdfDevis, setCurrentPdfDevis] = useState<FirestoreDevis | null>(null);
  const [showFdaPdfModal, setShowFdaPdfModal] = useState(false);
  const [fdaPdfPreviewUrl, setFdaPdfPreviewUrl] = useState<string | null>(null);
  const [devisToDelete, setDevisToDelete] = useState<FirestoreDevis | null>(null);
  const [contactModal, setContactModal] = useState<ContactModalType | null>(null);
  const [companiesMap, setCompaniesMap] = useState<any>({});
  const [filterType, setFilterType] = useState("");
  const [filterMonth, setFilterMonth] = useState<number | "all">(new Date().getMonth());
  const [filterYear, setFilterYear] = useState<number | "all">(new Date().getFullYear());
  const [sortColumn, setSortColumn] = useState<"date" | "updatedAt" | "amount" | "companyId" | "opportunityType" | "none">("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [devisPerPage, setDevisPerPage] = useState(10);

  // Pagination et filtrage
  const indexOfLastDevis = currentPage * devisPerPage;
  const indexOfFirstDevis = indexOfLastDevis - devisPerPage;

  // Fonction pour obtenir les types d'opportunités uniques
  const uniqueOpportunityTypes = useMemo(() => {
    const types = new Set(devisList.map(d => d.opportunityType));
    return Array.from(types).filter(Boolean);
  }, [devisList]);

  // Fonction pour obtenir les années disponibles
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const yearsFromData = new Set(devisList.map(d => {
      const date = d.date instanceof Timestamp ? d.date.toDate() : new Date(d.date);
      return date.getFullYear();
    }));
    
    // Ajouter les années courante, précédente et suivante pour garantir leur disponibilité
    const standardYears = [currentYear - 1, currentYear, currentYear + 1];
    standardYears.forEach(year => yearsFromData.add(year));
    
    return Array.from(yearsFromData).sort((a, b) => b - a);
  }, [devisList]);

  // Modification de la fonction de filtrage et ajout du tri
  const filtered = useMemo(() => {
    console.log("Début du filtrage. devisList.length:", devisList.length);
    const low = searchTerm.toLowerCase();
    let tempDevis = [...devisList]; // Crée une copie mutable

    // 1. Filtrer par terme de recherche (inclut l'ID)
    tempDevis = tempDevis.filter(
      (d) =>
        (d.companyId?.toLowerCase() || "").includes(low) ||
        (d.contact?.toLowerCase() || "").includes(low) ||
        (d.opportunityType?.toLowerCase() || "").includes(low) ||
        (d.amount?.toString() || "").includes(low) ||
        (d.id?.toLowerCase() || "").includes(low)
    );
    console.log("Après filtre de recherche. tempDevis.length:", tempDevis.length);

    // 2. Filtrer par type d'opportunité
    if (filterType && filterType !== "all") {
      tempDevis = tempDevis.filter((d) => d.opportunityType === filterType);
    }
    console.log("Après filtre par type. tempDevis.length:", tempDevis.length);

    // 3. Filtrer par mois et année
    if (filterMonth !== "all" || filterYear !== "all") {
      tempDevis = tempDevis.filter((d) => {
        const devisDate = d.date instanceof Timestamp ? d.date.toDate() : new Date(d.date);
        const devisMonth = devisDate.getMonth();
        const devisYear = devisDate.getFullYear();

        const matchMonth = filterMonth === "all" || devisMonth === Number(filterMonth);
        const matchYear = filterYear === "all" || devisYear === Number(filterYear);

        return matchMonth && matchYear;
      });
    }
    console.log("Après filtre par mois/année. tempDevis.length:", tempDevis.length);

    // 4. Trier les devis (le tri ne devrait pas changer la longueur)
    if (sortColumn !== "none") {
      tempDevis.sort((a, b) => {
        let valA: any, valB: any;

        if (sortColumn === "date" || sortColumn === "updatedAt") {
          valA = a[sortColumn] instanceof Timestamp ? a[sortColumn].toDate() : new Date((a as any)[sortColumn]);
          valB = b[sortColumn] instanceof Timestamp ? b[sortColumn].toDate() : new Date((b as any)[sortColumn]);
        } else {
          valA = (a as any)[sortColumn];
          valB = (b as any)[sortColumn];
        }

        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }
    console.log("Fin du filtrage. filtered.length:", tempDevis.length);

    return tempDevis;
  }, [devisList, searchTerm, filterType, filterMonth, filterYear, sortColumn, sortOrder]);

  const currentDevis = filtered.slice(indexOfFirstDevis, indexOfLastDevis);
  console.log("currentDevis.length après slice:", currentDevis.length);
  const totalPages = Math.ceil(filtered.length / devisPerPage);

  const nextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  // Calcul du montant total (sur les devis filtrés et triés)
  const totalAmount = useMemo(() => {
    return filtered.reduce((acc, devis) => acc + (devis.amount || 0), 0);
  }, [filtered]);

  // Détecter l'opportunité pré-sélectionnée depuis la navigation
  useEffect(() => {
    const state = location.state as { preselectedOpportunity?: any } | null;
    if (state?.preselectedOpportunity) {
      setPreselectedOpportunity(state.preselectedOpportunity);
      setShowModal(true);
      // Nettoyer le state pour éviter de rouvrir le modal à chaque re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Chargement des devis
  useEffect(() => {
    const docRef = doc(db, "prospects", companiesDocId);
    return onSnapshot(docRef, (snap) => {
      console.log("Snapshot reçu pour prospects/companies.");
      if (!snap.exists()) {
        console.log("Le document prospects/companies n'existe pas.");
        setDevisList([]);
        setCompaniesMap({});
        return;
      }
      const data = snap.data();
      console.log("Données brutes du document companies:", data);
      setCompaniesMap(data);
      const all: FirestoreDevis[] = [];
      for (const companyId in data) {
        const companyData = data[companyId];
        console.log(`Traitement de la société: ${companyId}`, companyData);
        const arr = companyData?.devis;
        const companyOpportunities = companyData?.opportunities;
        if (Array.isArray(arr)) {
          console.log(`Devis trouvés pour ${companyId}:`, arr.length);
          arr.forEach((d: FirestoreDevis) =>
            all.push({ ...d, companyId, society: companyData, associatedOpportunities: companyOpportunities })
          );
        } else {
          console.log(`Pas de tableau 'devis' trouvé ou n'est pas un tableau pour ${companyId}.`)
        }
      }
      setDevisList(all);
      console.log("Liste finale des devis chargée:", all.length, "devis.");
    });
  }, [companiesDocId]);

  // Gestion des devis
  const handleAddDevis = () => {
    setDevisToEdit(undefined);
    setPreselectedOpportunity(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setPreselectedOpportunity(null);
  };

  const handleEditDevis = (devis: FirestoreDevis) => {
    setDevisToEdit(devis);
    setShowModal(true);
  };

  const handleDeleteDevis = async (devis: FirestoreDevis) => {
    setDevisToDelete(devis);
  };

  const confirmDevisDelete = async () => {
    if (!devisToDelete) return;

    console.log("Tentative de suppression du devis:", devisToDelete);
    console.log("ID du devis à supprimer:", devisToDelete.id);
    console.log("Company ID:", devisToDelete.companyId);

    try {
      const docRef = doc(db, "prospects", companiesDocId);
      const snapshot = await getDoc(docRef);

      if (snapshot.exists()) {
        const data = snapshot.data();
        console.log("Données de la société avant filtrage:", data[devisToDelete.companyId]);
        const currentDevis = data[devisToDelete.companyId]?.devis || [];
        console.log("Tableau de devis actuel dans Firestore:", currentDevis);

        const updatedDevis = currentDevis.filter((item: any) => {
          console.log(`Comparaison: item.id='${item.id}' (Type: ${typeof item.id}) avec devisToDelete.id='${devisToDelete.id}' (Type: ${typeof devisToDelete.id})`);
          return item.id !== devisToDelete.id;
        });
        console.log("Tableau de devis après filtrage:", updatedDevis);

        if (updatedDevis.length === currentDevis.length) {
          console.warn("Aucun devis n'a été filtré. L'ID pourrait ne pas correspondre.");
          toast.error("Le devis n'a pas été trouvé pour la suppression. Vérifiez l'ID.");
        }

        await updateDoc(docRef, {
          [`${devisToDelete.companyId}.devis`]: updatedDevis,
        });
        toast.success("Devis supprimé avec succès.");
      } else {
        toast.error("Société introuvable pour ce devis.");
      }
    } catch (err: any) {
      console.error("Erreur suppression devis :", err);
      toast.error("Erreur lors de la suppression: " + err.message);
    } finally {
      setDevisToDelete(null);
    }
  };

  // Utilitaires PDF
  const rgbFromHex = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    return rgb(r, g, b);
  };

  const formatFieldValue = (value: any, fieldName?: string): string => {
    if (value instanceof Timestamp) {
      return value.toDate().toLocaleDateString("fr-FR");
    }
    if (value && typeof value.toDate === 'function') {
      return value.toDate().toLocaleDateString("fr-FR");
    }
    if (fieldName === 'amount' && typeof value === 'number') {
      return value.toFixed(2);
    }
    // Gestion des champs numériques avec décimales
        if (typeof value === 'number') {
          const decimalFields = ['amount', 'pu', 'ttc', 'ht', 'montant', 'prix', 'total', 'budget', 'cost', 'price'];
          const integerFields = ['qte'];
          
          if (fieldName && decimalFields.some(field => fieldName.toLowerCase().includes(field))) {
            return value.toFixed(2);
          }
          if (fieldName && integerFields.some(field => fieldName.toLowerCase().includes(field))) {
            return Math.round(value).toString();
          }
        }
    return value?.toString() || "";
  };

  // Fonction pour générer le nom de fichier PDF au format: Date Création_Numéro Devis_SOCIETE
  const generatePdfFileName = (devis: FirestoreDevis): string => {
    // Format de la date de création : JJ-MM-AAAA
    let dateStr = "";
    if (devis.date) {
      const date = devis.date instanceof Timestamp ? devis.date.toDate() : new Date(devis.date);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      dateStr = `${day}-${month}-${year}`;
    }

    // Numéro de devis (utiliser l'ID tel quel)
    const devisNumber = devis.id || "SANS_NUMERO";

    // Nom de la société (utiliser companyId ou society.name si disponible)
    const companyName = (devis.society as Society)?.name || devis.companyId || "SANS_SOCIETE";
    // Nettoyer le nom de la société pour le nom de fichier (enlever les caractères spéciaux)
    const cleanCompanyName = companyName.replace(/[^a-zA-Z0-9_-]/g, '_');

    // Format final : Date Création_Numéro Devis_SOCIETE
    return `${dateStr}_${devisNumber}_${cleanCompanyName}.pdf`;
  };

  // Fonction pour générer le nom de fichier PDF FDA au format: Hillebrand-RegExport Conseil_Date_Client (nom société)
  const generateFdaPdfFileName = (devis: FirestoreDevis): string => {
    // Format de la date : JJ-MM-AAAA
    let dateStr = "";
    if (devis.date) {
      const date = devis.date instanceof Timestamp ? devis.date.toDate() : new Date(devis.date);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      dateStr = `${day}-${month}-${year}`;
    }

    // Nom de la société (utiliser society.name si disponible, sinon companyId)
    const companyName = (devis.society as Society)?.name || devis.companyId || "SANS_SOCIETE";
    // Nettoyer le nom de la société pour le nom de fichier (enlever les caractères spéciaux)
    const cleanCompanyName = companyName.replace(/[^a-zA-Z0-9_-]/g, '_');

    // Format final : Hillebrand-RegExport Conseil_Date_Client (nom société)
    return `Hillebrand-RegExport Conseil_${dateStr}_${cleanCompanyName}.pdf`;
  };

  // Mapping entre les types de documents PDF et leurs configurations
  const getPdfConfigByDocumentType = (pdfDocumentType: string): { pdfPath: string; coordinatesDoc: string } => {
    const pdfConfigMap: Record<string, { pdfPath: string; coordinatesDoc: string }> = {
      "Devis REC TTB": {
        pdfPath: "/pdfs/ttbRegistration1.pdf",
        coordinatesDoc: "mondevispositions"
      },
      "Devis REC FDA Enregistrement + Gestion": {
        pdfPath: "/pdfs/fdaRegistration&Management.pdf",
        coordinatesDoc: "fdaPositions"
      },
      "Devis REC FDA Gestion": {
        pdfPath: "/pdfs/fdaManagement.pdf",
        coordinatesDoc: "fdaManagementPositions"
      },
      "Devis REC Conseil réglementaire": {
        pdfPath: "/pdfs/ContratCadre.pdf",
        coordinatesDoc: "contratCadrePositions"
      },
      "Devis REC CIFER China Registration": {
        pdfPath: "/pdfs/ciferChinaRegistration.pdf",
        coordinatesDoc: "ciferChinaRegistrationPositions"
      }
    };

    // Retourner la configuration si elle existe, sinon utiliser la configuration par défaut (TTB)
    return pdfConfigMap[pdfDocumentType] || {
      pdfPath: "/pdfs/ttbRegistration1.pdf",
      coordinatesDoc: "mondevispositions"
    };
  };

  // Génération PDF standard
  const fillPdfWithData = async (selectedDevis: FirestoreDevis) => {
    try {
      setCurrentPdfDevis(selectedDevis);
      const scale = PDF_SCALE;
      
      // Obtenir la configuration PDF en fonction du type de document choisi, ou par défaut basé sur le type d'opportunité
      const pdfDocumentType = (selectedDevis as any).pdfDocumentType;
      let pdfConfig;
      
      if (pdfDocumentType) {
        // Utiliser le type de document choisi par l'utilisateur
        pdfConfig = getPdfConfigByDocumentType(pdfDocumentType);
        console.log(`Génération PDF avec le document choisi "${pdfDocumentType}" et le fichier: ${pdfConfig.pdfPath}`);
      } else {
        // Fallback : utiliser le type d'opportunité pour la rétrocompatibilité
        const legacyMap: Record<string, { pdfPath: string; coordinatesDoc: string }> = {
          "Enregistrement TTB avec gestion CBMA": {
            pdfPath: "/pdfs/ttbRegistration1.pdf",
            coordinatesDoc: "mondevispositions"
          },
          "Enregistrement FDA (USA)": {
            pdfPath: "/pdfs/fdaRegistration&Management.pdf",
            coordinatesDoc: "fdaPositions"
          },
          "Enregistrement FDA + Gestion (USA)": {
            pdfPath: "/pdfs/fdaRegistration&Management.pdf",
            coordinatesDoc: "fdaPositions"
          },
          "Gestion FDA (USA)": {
            pdfPath: "/pdfs/fdaManagement.pdf",
            coordinatesDoc: "fdaManagementPositions"
          },
          "Conseil réglementaire": {
            pdfPath: "/pdfs/ContratCadre.pdf",
            coordinatesDoc: "contratCadrePositions"
          }
        };
        pdfConfig = legacyMap[selectedDevis.opportunityType || ""] || {
          pdfPath: "/pdfs/ttbRegistration1.pdf",
          coordinatesDoc: "mondevispositions"
        };
        console.log(`Génération PDF (legacy) pour le type "${selectedDevis.opportunityType}" avec le fichier: ${pdfConfig.pdfPath}`);
      }
      
      const pdfPath = pdfConfig.pdfPath;
      
      const existingPdfBytes = await fetch(pdfPath).then((res) => {
        if (!res.ok) throw new Error(`Échec du chargement du PDF: ${res.status} - ${pdfPath}`);
        return res.arrayBuffer();
      });
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      
      // Charger Helvetica pour tous les champs
      const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const fontItalic = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
      const fontBoldItalic = await pdfDoc.embedFont(StandardFonts.HelveticaBoldOblique);
      
      const pages = pdfDoc.getPages();
      
      // Charger toutes les coordonnées du document spécifique au type de devis
      const coords = await getPDFCoordinatesFromStorage(pdfConfig.coordinatesDoc);
      console.log(`Coordonnées chargées depuis "${pdfConfig.coordinatesDoc}" pour le type "${selectedDevis.opportunityType}"`);

      for (const [pageNumber, pageCoords] of Object.entries(coords)) {
        const pageIndex = parseInt(pageNumber) - 1;
        if (pageIndex < 0 || pageIndex >= pages.length) continue;

        const page = pages[pageIndex];

        for (const [fullFieldName, fieldConfig] of Object.entries(pageCoords)) {
          const { x, y, size = 12, color, bold, italic, customText, isCustomField } = fieldConfig as FieldData;
          
          const [source, fieldName] = fullFieldName.includes('-') 
            ? fullFieldName.split('-') as [FieldSource, string]
            : ['devis', fullFieldName];

          let value = '';
          console.log(`Traitement champ: ${fullFieldName}, source: ${source}, fieldName: ${fieldName}`);
          
          // Log spécial pour identifier le champ pu problématique
          if (fieldName === 'pu') {
            console.log(`⚠️ CHAMP PU DÉTECTÉ - fullFieldName: ${fullFieldName}, source: ${source}`);
            console.log(`selectedDevis:`, selectedDevis);
            console.log(`associatedOpportunities:`, selectedDevis.associatedOpportunities);
          }
          
          // Optionnel : Ignorer le champ pu si vous ne voulez pas qu'il s'affiche
          // Décommentez la ligne suivante si vous voulez bloquer l'affichage du champ pu
          // if (fieldName === 'pu') continue;
          
          // Gestion des champs texte personnalisés
          if (isCustomField && customText) {
            value = customText;
            console.log(`Champ texte personnalisé: ${customText}`);
          } else {
            // Logique normale pour les champs de données
          switch (source) {
            case 'devis':
              // Essayer d'abord directement sur selectedDevis
              let devisValue = (selectedDevis as any)[fieldName];
              
              // Si pas trouvé et que c'est un champ d'opportunité, essayer dans associatedOpportunities
              if (!devisValue && selectedDevis.associatedOpportunities) {
                const matchingOpp = selectedDevis.associatedOpportunities.find(
                  (opp: any) => opp.type === selectedDevis.opportunityType
                );
                devisValue = matchingOpp?.[fieldName];
              }
              
              // Si toujours pas trouvé, essayer des mappings spécifiques
              if (!devisValue) {
                switch (fieldName) {
                  case 'type':
                    // Utiliser le texte personnalisé s'il existe, sinon le type par défaut
                    devisValue = (selectedDevis as any).customTypeText || selectedDevis.opportunityType;
                    break;
                  case 'amount2':
                    // amount2 est un alias pour amount, permet d'avoir le même champ deux fois
                    devisValue = selectedDevis.amount;
                    break;
                  case 'pu':
                  case 'qte':
                  case 'ttc':
                    // Ces champs sont souvent dans les opportunités
                    if (selectedDevis.associatedOpportunities) {
                      const matchingOpp = selectedDevis.associatedOpportunities.find(
                        (opp: any) => opp.type === selectedDevis.opportunityType
                      );
                      devisValue = matchingOpp?.[fieldName];
                    }
                    break;
                }
              }
              
              value = formatFieldValue(devisValue, fieldName);
              console.log(`Valeur devis pour ${fieldName}:`, value, 'devisValue:', devisValue);
              break;
            case 'opportunities':
              // Essayer d'abord selectedDevis.opportunity, puis associatedOpportunities
              let opportunityData = selectedDevis.opportunity;
              if (!opportunityData && selectedDevis.associatedOpportunities) {
                // Chercher l'opportunité correspondante par type
                opportunityData = selectedDevis.associatedOpportunities.find(
                  (opp: any) => opp.type === selectedDevis.opportunityType
                );
              }
              // Pour le champ 'type' dans opportunities, utiliser le texte personnalisé si disponible
              if (fieldName === 'type' && (selectedDevis as any).customTypeText) {
                value = formatFieldValue((selectedDevis as any).customTypeText, fieldName);
              } else {
                value = formatFieldValue(opportunityData?.[fieldName], fieldName);
              }
              console.log(`Valeur opportunities pour ${fieldName}:`, value, 'opportunityData:', opportunityData);
              break;
            case 'society':
              value = formatFieldValue(
                selectedDevis.society ? (selectedDevis.society as Society)[fieldName] : null,
                fieldName
              );
              console.log(`Valeur society pour ${fieldName}:`, value);
              break;
            }
          }

          // Vérifier que la valeur n'est pas vide, null, undefined, ou juste des espaces
          // Éviter aussi les valeurs de test par défaut
          if (value && 
              value.toString().trim() !== '' && 
              value !== 'undefined' && 
              value !== 'null' &&
              value !== 'test' &&
              value !== '0' &&
              value !== '0.00') {
            // Formatage final pour s'assurer que les valeurs numériques ont le bon format
            let finalValue = value;
            if (typeof value === 'string' && !isNaN(parseFloat(value))) {
              const numValue = parseFloat(value);
              if (numValue > 0) {
                // Champs avec décimales
                if (fieldName === 'pu' || fieldName === 'ttc' || fieldName === 'amount') {
                  finalValue = numValue.toFixed(2);
                }
                // Champs entiers (comme qte)
                else if (fieldName === 'qte') {
                  finalValue = Math.round(numValue).toString();
                }
              }
            }
            
            const adjustedX = x / scale;
            const adjustedY = y / scale;
            let adjustedSize = size * PDF_FONT_SIZE_OFFSET;
            
            let font = fontRegular;
            
            // Augmenter la taille pour l'ID
            if (fieldName === 'id') {
              adjustedSize = adjustedSize * 1.2; // Augmenter la taille de 20% pour l'ID
            }
            
            // Respecter les paramètres bold/italic pour tous les champs (Helvetica)
            if (bold && italic) font = fontBoldItalic;
            else if (bold) font = fontBold;
            else if (italic) font = fontItalic;

            // Gestion du saut de ligne pour le champ 'type' si plus de 4 mots
            const isTypeField = fieldName === 'type';
            if (isTypeField && typeof finalValue === 'string') {
              const words = finalValue.trim().split(/\s+/);
              if (words.length > 4) {
                // Diviser en deux lignes : 4 premiers mots sur la première ligne, le reste sur la seconde
                const firstLine = words.slice(0, 4).join(' ');
                const secondLine = words.slice(4).join(' ');
                const lineHeight = adjustedSize * 1.2; // Espacement entre les lignes
                
                // Dessiner la première ligne
                page.drawText(firstLine, {
                  x: adjustedX,
                  y: adjustedY,
                  size: adjustedSize,
                  font,
                  color: color ? rgbFromHex(color) : rgb(0, 0, 0),
                });
                
                // Dessiner la deuxième ligne (en dessous)
                page.drawText(secondLine, {
                  x: adjustedX,
                  y: adjustedY - lineHeight,
                  size: adjustedSize,
                  font,
                  color: color ? rgbFromHex(color) : rgb(0, 0, 0),
                });
              } else {
                // Texte normal si 4 mots ou moins
                page.drawText(finalValue, {
                  x: adjustedX,
                  y: adjustedY,
                  size: adjustedSize,
                  font,
                  color: color ? rgbFromHex(color) : rgb(0, 0, 0),
                });
              }
            } else {
              // Affichage normal pour les autres champs
              page.drawText(finalValue, {
                x: adjustedX,
                y: adjustedY,
                size: adjustedSize,
                font,
                color: color ? rgbFromHex(color) : rgb(0, 0, 0),
              });
            }
          }
        }
      }

      const pdfBytes = await pdfDoc.save();
      // @ts-ignore
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      setPdfPreviewUrl(blobUrl);
      setShowPdfModal(true);
      toast.success("PDF généré avec succès !");
    } catch (error) {
      console.error("Erreur génération PDF:", error);
      toast.error("Échec de la génération du PDF");
    }
  };

  // Génération PDF FDA - Même logique que DevisFoodinnov
  const fillFdaPdfWithData = async (selectedDevis: FirestoreDevis) => {
    try {
      setCurrentPdfDevis(selectedDevis);
      
      const pdfPath = "/pdfs/REC_hillebrandfdaregistration.pdf";
      const existingPdfBytes = await fetch(pdfPath).then((res) => {
        if (!res.ok) throw new Error(`Échec du chargement du PDF: ${res.status}`);
        return res.arrayBuffer();
      });
      
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const pages = pdfDoc.getPages();
      if (pages.length === 0) throw new Error("Le PDF n'a aucune page");

      const coords = await getPDFCoordinatesFromStorage("fdaPositionsREC");
      console.log("Coordonnées FDA chargées:", coords);

      for (const [pageNumber, pageCoords] of Object.entries(coords)) {
        const pageIndex = parseInt(pageNumber) - 1;
        if (pageIndex < 0 || pageIndex >= pages.length) continue;

        const page = pages[pageIndex];
        const { height } = page.getSize();

        for (const [fullFieldName, fieldConfig] of Object.entries(pageCoords)) {
          const { x, y, size = 12, color = "#000000", bold = false, italic = false, customText, isCustomField } = fieldConfig;
          
          let value = '';

          // Gestion des champs texte personnalisés
          if (isCustomField && customText) {
            value = customText;
            console.log(`Champ texte personnalisé FDA: ${customText}`);
          } else {
            const [source, fieldName] = fullFieldName.includes('-') 
              ? fullFieldName.split('-') as [FieldSource, string]
              : ['devis', fullFieldName];

            switch (source) {
              case 'devis':
                value = formatFieldValue((selectedDevis as any)[fieldName], fieldName);
                break;
              case 'society':
                const society = selectedDevis.society as Society | undefined;
                if (fieldName === 'companyName') {
                  value = selectedDevis.companyId;
                } else if (fieldName === 'address') {
                  value = society?.address || '';
                } else if (fieldName === 'postalCode') {
                  value = society?.postalCode || '';
                } else if (fieldName === 'city') {
                  value = society?.city || '';
                } else if (fieldName === 'country') {
                  value = society?.country || '';
                } else {
                  value = society ? formatFieldValue(society[fieldName], fieldName) : '';
                }
                break;
              case 'opportunities':
                value = formatFieldValue((selectedDevis.opportunity as Opportunity)?.[fieldName], fieldName);
                break;
              case 'hillebrand':
                if (fieldName === 'companyName') {
                  value = selectedDevis.companyId;
                } else if (fieldName === 'address') {
                  value = selectedDevis.society?.address || '';
                } else if (fieldName === 'postalCode') {
                  value = selectedDevis.society?.postalCode || '';
                } else if (fieldName === 'city') {
                  value = selectedDevis.society?.city || '';
                } else if (fieldName === 'country') {
                  value = selectedDevis.society?.country || '';
                } else if (fieldName === 'registrationNumber') {
                  value = selectedDevis.id || '';
                }
                break;
            }
          }

          if (value) {
            // Formatage final pour s'assurer que les montants ont 2 décimales
            let finalValue = value;
            if (typeof value === 'string' && !isNaN(parseFloat(value))) {
              const numValue = parseFloat(value);
              if (numValue > 0 && (fullFieldName?.toLowerCase().includes('amount') || 
                                  fullFieldName?.toLowerCase().includes('montant') || 
                                  fullFieldName?.toLowerCase().includes('prix') || 
                                  fullFieldName?.toLowerCase().includes('ttc') || 
                                  fullFieldName?.toLowerCase().includes('ht'))) {
                finalValue = numValue.toFixed(2);
              }
            }
            
            const font = await (() => {
              if (bold && italic) return pdfDoc.embedFont(StandardFonts.HelveticaBoldOblique);
              if (bold) return pdfDoc.embedFont(StandardFonts.HelveticaBold);
              if (italic) return pdfDoc.embedFont(StandardFonts.HelveticaOblique);
              return pdfDoc.embedFont(StandardFonts.Helvetica);
            })();

            // Utiliser la même logique que pour les autres devis (avec PDF_SCALE)
            const scale = PDF_SCALE;
            const adjustedX = x / scale;
            const adjustedY = y / scale;
            const adjustedSize = size * PDF_FONT_SIZE_OFFSET;

            page.drawText(finalValue, {
              x: adjustedX,
              y: adjustedY,
              size: adjustedSize,
              font,
              color: rgbFromHex(color),
            });
          }
        }
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      setFdaPdfPreviewUrl(blobUrl);
      setShowFdaPdfModal(true);
      toast.success("PDF FDA généré avec succès !");
    } catch (error) {
      console.error("Erreur génération PDF FDA:", error);
      toast.error(`Erreur FDA PDF: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const handleExportCSV = () => {
    const headers = [
      "ID",
      "Société",
      "Contact",
      "Type",
      "Montant",
      "Date",
      "Édité le",
      "Opportunité"
    ];

    const csvData = filtered.map((d) => [
      d.id || "-",
      d.companyId,
      d.contact,
      d.opportunityType,
      `${formatFieldValue(d.amount, 'amount')} €`,
      formatFieldValue(d.date),
      d.updatedAt ? formatFieldValue(d.updatedAt) : "-",
      d.opportunity ? formatFieldValue(d.opportunity.type) : "-"
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `devis_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openContactModal = (devis: FirestoreDevis) => {
    const companyInfo = companiesMap[devis.companyId];
    setContactModal({
      name: devis.contact ?? "Contact inconnu",
      email: companyInfo?.principalEmail ?? "Non renseigné",
      phone: companyInfo?.principalPhone ?? "Non renseigné",
      fonction: companyInfo?.principalPosition ?? "",
      gender: companyInfo?.principalGender,
      principalContact: companyInfo?.principalContact,
      principalFirstName: companyInfo?.principalFirstName,
    });
  };

  const closeContactModal = () => {
    setContactModal(null);
  };

  return (
    <div className="devis-container" style={isDemoMode ? { paddingTop: '120px' } : undefined}>
      <ToastContainer position="top-right" autoClose={2000} />
      <Header 
        isAuthenticated={true} 
        onLoginClick={() => navigate('/login')} 
        onHomeClick={() => navigate('/')} 
        onLogoutClick={handleLogout}
      />
      <h2 className="devis-title" style={{ color: '#424272' }}>Liste des Devis :</h2>

      <div className="actions-bar">
        <button className="add-societe-button" onClick={handleAddDevis}>
          + Ajouter un Devis
        </button>
        <button className="add-societe-button" onClick={handleExportCSV}>
          Exporter en CSV
        </button>

        <select
          className="sort-dropdown"
          value={filterType}
          onChange={(e) => {
            setFilterType(e.target.value);
            setCurrentPage(1);
          }}
          style={{ maxWidth: "180px" }}
        >
          <option value="">Filtrer par type --</option>
          <option value="all">Tous les types</option>
          {uniqueOpportunityTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>

        <input
          type="text"
          className="search-bar"
          placeholder="Rechercher... (ID, Société, Contact, Type, Montant)"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />

        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <button type="button" title="Mois précédent" onClick={() => { if (filterMonth === 'all') return; const m = Number(filterMonth); setFilterMonth(m === 0 ? 11 : m - 1); setCurrentPage(1); }} style={{ cursor: 'pointer', border: '1px solid #ced4da', background: '#fff', borderRadius: '4px', padding: '4px 8px', color: '#424272', fontWeight: 700, lineHeight: 1 }}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <select
            className="sort-dropdown"
            style={{ maxWidth: "130px" }}
            value={filterMonth}
            onChange={(e) => {
              setFilterMonth(e.target.value === "all" ? "all" : Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value="all">Afficher Tout</option>
            {[...Array(12).keys()].map((monthIndex) => (
              <option key={monthIndex} value={monthIndex}>
                {new Date(0, monthIndex).toLocaleString("fr-FR", { month: "long" }).replace(/^\w/, c => c.toUpperCase())}
              </option>
            ))}
          </select>
          <button type="button" title="Mois suivant" onClick={() => { if (filterMonth === 'all') return; const m = Number(filterMonth); setFilterMonth(m === 11 ? 0 : m + 1); setCurrentPage(1); }} style={{ cursor: 'pointer', border: '1px solid #ced4da', background: '#fff', borderRadius: '4px', padding: '4px 8px', color: '#424272', fontWeight: 700, lineHeight: 1 }}>
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>

        <select
          className="sort-dropdown"
          style={{ maxWidth: "120px" }}
          value={filterYear}
          onChange={(e) => {
            setFilterYear(e.target.value === "all" ? "all" : Number(e.target.value));
            setCurrentPage(1);
          }}
        >
          <option value="all">Afficher Tout</option>
          {availableYears.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>

        <select
          className="sort-dropdown"
          style={{ maxWidth: "160px" }}
          value={sortColumn}
          onChange={(e) => {
            setSortColumn(e.target.value as "date" | "updatedAt" | "amount" | "companyId" | "opportunityType" | "none");
            setCurrentPage(1);
          }}
        >
          <option value="date">Date de création</option>
          <option value="updatedAt">Date d'édition</option>
          <option value="amount">Montant</option>
          <option value="companyId">Société</option>
          <option value="opportunityType">Type</option>
        </select>

        <button
          className="direction-button"
          onClick={() =>
            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
          }
        >
          {sortOrder === "asc" ? "↑" : "↓"}
        </button>

        <select
          className="sort-dropdown"
          style={{ maxWidth: "130px" }}
          value={devisPerPage}
          onChange={(e) => {
            setDevisPerPage(Number(e.target.value));
            setCurrentPage(1);
          }}
        >
          <option value={5}>5 par page</option>
          <option value={10}>10 par page</option>
          <option value={20}>20 par page</option>
          <option value={50}>50 par page</option>
        </select>
      </div>

      <div className="devis-content">
        <div className="devis-table-container">
          <table className="opportunites-table">
            <thead>
              <tr>
                <th>Numéro</th>
                <th>Société</th>
                <th>Contact</th>
                <th>Type</th>
                <th>Montant</th>
                <th>Date</th>
                <th>Édité le</th>
                <th>Documents</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentDevis.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: "center" }}>
                    Aucun devis.
                  </td>
                </tr>
              ) : (
                currentDevis.map((d, i) => (
                  <tr key={i}>
                    <td>{d.id || "-"}</td>
                    <td>{d.companyId}</td>
                    <td>
                      <button onClick={() => openContactModal(d)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#007bff",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        {d.contact}
                      </button>
                    </td>
                    <td>{d.opportunityType}</td>
                    <td>{d.amount} €</td>
                    <td>
                      {d.date instanceof Timestamp
                        ? d.date.toDate().toLocaleDateString("fr-FR")
                        : new Date(d.date).toLocaleDateString("fr-FR")}
                    </td>
                    <td>
                      {d.updatedAt
                        ? d.updatedAt instanceof Timestamp
                          ? d.updatedAt.toDate().toLocaleDateString("fr-FR")
                          : new Date(d.updatedAt).toLocaleDateString("fr-FR")
                        : "-"}
                    </td>
                    <td className="documents-column">
                      <i
                        className="bi bi-filetype-pdf"
                        title="PDF Standard"
                        onClick={() => fillPdfWithData(d)}
                        style={{ cursor: "pointer", marginRight: "10px", fontSize: "1.2rem", color: "#0d6efd" }}
                      />
                      {(d.opportunityType === "Enregistrement FDA (USA)" || 
                        d.opportunityType === "Enregistrement FDA + Gestion (USA)" ||
                        (d as any).pdfDocumentType === "Devis REC FDA Enregistrement + Gestion" ||
                        (d as any).pdfDocumentType === "Devis REC FDA Gestion") && (
                        <i
                          className="bi bi-filetype-pdf"
                          title="PDF FDA"
                          onClick={() => fillFdaPdfWithData(d)}
                          style={{ color: 'red', cursor: "pointer", fontSize: "1.2rem", marginRight: "10px" }}
                        />
                      )}
                    </td>
                    <td className="actions-column">
                      <button className="edit-button" onClick={() => handleEditDevis(d)}><i className="bi bi-pencil-square"></i></button>
                      <button className="delete-button" onClick={() => handleDeleteDevis(d)}><i className="bi bi-trash"></i></button>
                      <button 
                        className="edit-button" 
                        onClick={() => navigate('/factures', { state: { preselectedDevis: d } })} 
                        title="Créer une facture pour ce devis"
                        style={{ marginLeft: '5px' }}
                      >
                        <i className="bi bi-arrow-right"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
              <tr style={{ fontWeight: "bold", background: "#f8f8f8" }}>
                <td colSpan={4}>Total</td>
                <td>{totalAmount.toFixed(2)} €</td>
                <td colSpan={4}></td>
              </tr>
            </tbody>
          </table>

          <div className="pagination">
            <button onClick={prevPage} disabled={currentPage === 1} className="pagination-button">← Précédent</button>
            <span>Page {currentPage} / {totalPages}</span>
            <button onClick={nextPage} disabled={currentPage === totalPages} className="pagination-button">Suivant →</button>
          </div>
        </div>
      </div>

      <DevisModal 
        show={showModal} 
        handleClose={handleCloseModal} 
        initialData={devisToEdit}
        preselectedOpportunity={preselectedOpportunity}
      />
      
      {showPdfModal && pdfPreviewUrl && (
        <div className="pdf-modal-overlay">
          <div className="pdf-modal-content">
            <div className="pdf-modal-header">
              <h2>Aperçu Devis #{currentPdfDevis?.id}</h2>
              <button className="close-btn" onClick={() => setShowPdfModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <iframe
                src={pdfPreviewUrl}
                title="Aperçu PDF"
                width="100%"
              />
            </div>
            <div className="pdf-modal-footer">
              <button onClick={() => setShowPdfModal(false)} className="button">Fermer</button>
              <a
                href={pdfPreviewUrl}
                download={currentPdfDevis ? generatePdfFileName(currentPdfDevis) : 'Devis_sans_nom.pdf'}
                className="download-btn"
              >
                Télécharger PDF
              </a>
            </div>
          </div>
        </div>
      )}

      {showFdaPdfModal && fdaPdfPreviewUrl && (
        <div className="pdf-modal-overlay">
          <div className="pdf-modal-content">
            <div className="pdf-modal-header">
              <h2>Aperçu Devis FDA #{currentPdfDevis?.id}</h2>
              <button className="close-btn" onClick={() => setShowFdaPdfModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <iframe
                src={fdaPdfPreviewUrl}
                title="Aperçu PDF FDA"
                width="100%"
              />
            </div>
            <div className="pdf-modal-footer">
              <button onClick={() => setShowFdaPdfModal(false)} className="button">Fermer</button>
              <a
                href={fdaPdfPreviewUrl}
                download={currentPdfDevis ? generateFdaPdfFileName(currentPdfDevis) : 'Devis_FDA_sans_nom.pdf'}
                className="download-btn"
              >
                Télécharger PDF FDA
              </a>
            </div>
          </div>
        </div>
      )}

      {devisToDelete && (
        <div className="modal-overlay">
          <div className="delete-confirmation">
            <h2>Confirmer la suppression</h2>
            <p>Êtes-vous sûr de vouloir supprimer le devis "{devisToDelete?.opportunityType ?? ""}" pour la société "{devisToDelete?.companyId ?? ""}" ?</p>
            <p className="warning-text">Cette action est irréversible.</p>
            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setDevisToDelete(null)}>
                Annuler
              </button>
              <button className="delete-button" onClick={confirmDevisDelete}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {contactModal && (
        <div className="contact-popup-overlay" onClick={closeContactModal}>
          <div className="contact-popup-card" onClick={(e) => e.stopPropagation()}>
            <button className="close-popup-btn" onClick={closeContactModal}>✕</button>
            <div className="contact-popup-content">
              <div className={`contact-avatar ${
                contactModal.gender === "Mme" ? "avatar-female"
                : contactModal.gender === "Mr" ? "avatar-male"
                : "avatar-neutral"
              }`}>
                {((contactModal.principalContact || "").charAt(0) + (contactModal.principalFirstName || "").charAt(0)).toUpperCase()}
              </div>
              <h3>{contactModal.name}</h3>
              {contactModal.fonction && <p className="contact-role">{contactModal.fonction}</p>}
              <hr />
              <div className="contact-info">
                <p><strong>Téléphone :</strong> {contactModal.phone || "Non renseigné"}</p>
                <p><strong>Email :</strong> {contactModal.email || "Non renseigné"}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default Devis;